import { getApiBaseUrl } from "../config/runtimeConfig";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export type TaskStatus = "ToDo" | "Waiting" | "InProgress" | "Done";
export type TaskUrgency = "Normal" | "Urgent" | "VeryUrgent";

/** Uma tarefa da lista de gestão — GET /api/tasks. */
export interface TaskItem {
  id: number;
  title: string;
  description: string | null;
  status: TaskStatus;
  urgency: TaskUrgency;
  /** null = por atribuir. */
  responsibleUserId: string | null;
  responsibleName: string | null;
  /** ISO; só a parte da data conta. */
  dueDate: string | null;
  workId: number | null;
  workName: string | null;
  completedAt: string | null;
  created: string;
  /** Tarefa macro do calendário que gerou esta; null = criada à mão. */
  recurringTaskId: number | null;
}

export interface TaskAssignee {
  userId: string;
  name: string;
}

/** Corpo do PUT: a linha inteira, porque a grelha grava célula a célula sobre a mesma linha. */
export interface TaskPayload {
  title: string;
  description: string | null;
  status: TaskStatus;
  urgency: TaskUrgency;
  responsibleUserId: string | null;
  dueDate: string | null;
  workId: number | null;
}

export type TaskScope = "open" | "done" | "all";

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export function getTasks(filters: { scope: TaskScope; search?: string }): Promise<TaskItem[]> {
  const params = new URLSearchParams();
  params.set("scope", filters.scope);
  if (filters.search && filters.search.trim() !== "") {
    params.set("search", filters.search.trim());
  }
  return request<TaskItem[]>(`/api/tasks?${params.toString()}`);
}

export function getTaskAssignees(): Promise<TaskAssignee[]> {
  return request<TaskAssignee[]>("/api/tasks/assignees");
}

/** O número ao lado de "Tarefas" no menu; `today` é o dia de quem pede ("YYYY-MM-DD"). */
export function getTaskSummary(today: string): Promise<{ mineDueOrOverdue: number }> {
  return request<{ mineDueOrOverdue: number }>(`/api/tasks/summary?today=${encodeURIComponent(today)}`);
}

/** Só o título é obrigatório; o servidor põe "Por fazer", urgência normal e quem cria como responsável. */
export function createTask(payload: Partial<TaskPayload> & { title: string }): Promise<TaskItem> {
  return request<TaskItem>("/api/tasks", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**
 * Grava só os campos enviados (PATCH com a lista de campos): duas pessoas a mexer na mesma
 * tarefa, uma na data e outra na urgência, não se apagam uma à outra.
 */
export function updateTask(id: number, changes: Partial<TaskPayload>): Promise<TaskItem> {
  return request<TaskItem>(`/api/tasks/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...changes, fields: Object.keys(changes) }),
  });
}

export function deleteTask(id: number): Promise<void> {
  return request<void>(`/api/tasks/${id}`, { method: "DELETE" });
}

/** Uma tarefa macro do calendário: o que se repete todos os meses (ou de N em N meses) no mesmo dia. */
export interface RecurringTask {
  id: number;
  title: string;
  description: string | null;
  dayOfMonth: number;
  intervalMonths: number;
  /** "Todos os meses", "Trimestral", … */
  frequencyLabel: string;
  /** Dia 1 do primeiro mês (ISO). */
  firstMonth: string;
  urgency: TaskUrgency;
  responsibleUserId: string | null;
  responsibleName: string | null;
  workId: number | null;
  workName: string | null;
}

export interface RecurringTaskPayload {
  title: string;
  description: string | null;
  dayOfMonth: number;
  intervalMonths: number;
  /** "YYYY-MM-DD" de qualquer dia do primeiro mês; null = o servidor escolhe (este mês ou o seguinte). */
  firstMonth: string | null;
  urgency: TaskUrgency;
  responsibleUserId: string | null;
  workId: number | null;
}

export interface RecurringOccurrence {
  recurringTaskId: number;
  date: string;
  taskId: number | null;
  taskStatus: TaskStatus | null;
}

export interface TaskCalendar {
  month: string;
  macros: RecurringTask[];
  occurrences: RecurringOccurrence[];
  /** Tarefas com data no mês que não vieram de uma macro. */
  tasks: TaskItem[];
}

/** `month` = "YYYY-MM". */
export function getTaskCalendar(month: string): Promise<TaskCalendar> {
  return request<TaskCalendar>(`/api/tasks/calendar?month=${encodeURIComponent(month)}`);
}

export function saveRecurringTask(id: number | null, payload: RecurringTaskPayload): Promise<RecurringTask> {
  return request<RecurringTask>(id === null ? "/api/tasks/recurring" : `/api/tasks/recurring/${id}`, {
    method: id === null ? "POST" : "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function deleteRecurringTask(id: number): Promise<void> {
  return request<void>(`/api/tasks/recurring/${id}`, { method: "DELETE" });
}

export function payloadOf(task: TaskItem): TaskPayload {
  return {
    title: task.title,
    description: task.description,
    status: task.status,
    urgency: task.urgency,
    responsibleUserId: task.responsibleUserId,
    dueDate: task.dueDate ? task.dueDate.slice(0, 10) : null,
    workId: task.workId,
  };
}
